import React from 'react';
import DemoIcon from '../assets/images/svgs/demo-icon.svg';

export const HeroSectionSkeleton: React.FC<{ padding?: string }> = ({ padding = 'pt-24 pb-12 md:pt-12' }) => (
  <div className="bg-khLightGray">
    <div className={`max-w-page mx-auto px-6 ${padding}`}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-16">
        <div
          className="hidden h-[150px] w-[150px] shrink-0 animate-pulse rounded-full bg-gray-200 md:block"
          aria-hidden
        />
        <div className="flex-1 text-left">
          <div className="mb-3">
            <div className="h-5 w-24 animate-pulse rounded bg-gray-200" aria-hidden />
          </div>
          <div className="mb-4 flex items-center justify-start gap-4">
            <div className="h-8 w-3/4 max-w-md animate-pulse rounded bg-gray-200 md:h-9" aria-hidden />
          </div>
          <div className="flex flex-col gap-2">
            <div className="h-4 w-full max-w-2xl animate-pulse rounded bg-gray-200" aria-hidden />
            <div className="h-4 w-4/5 max-w-xl animate-pulse rounded bg-gray-200" aria-hidden />
          </div>
        </div>
      </div>
    </div>
  </div>
);

interface HeroSectionProps {
  title: string;
  description?: string;
  icon?: string | React.ReactNode | null;
  iconAlt?: string;
  eyebrowText?: string;
  favoriteButton?: React.ReactNode;
  iconCircleBg?: string; // Tailwind class for circular icon background (e.g., 'bg-peersonaCircle')
  padding?: string;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  title,
  description,
  icon = DemoIcon,
  iconAlt = 'Icon',
  eyebrowText,
  favoriteButton,
  iconCircleBg,
  padding = 'pt-24 pb-12 md:pt-12',
}) => {
  const renderIcon = () => {
    if (!icon) return null;

    if (iconCircleBg) {
      // Circular icon with background
      return (
        <div className="relative hidden flex-shrink-0 md:flex">
          <div className="h-37.5 w-37.5 relative">
            <div
              className={`${iconCircleBg} flex h-full w-full items-center justify-center overflow-hidden rounded-full`}
            >
              {typeof icon === 'string' ? (
                <img src={icon} alt={iconAlt} className="h-full w-full" />
              ) : (
                <div className="h-full w-full [&>svg]:h-full [&>svg]:w-full">{icon}</div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Simple icon
    if (typeof icon === 'string') {
      return <img src={icon} alt={iconAlt} className="hidden h-[150px] w-[150px] shrink-0 md:block" />;
    }
    return <div className="hidden h-[150px] w-[150px] shrink-0 md:block [&>svg]:h-full [&>svg]:w-full">{icon}</div>;
  };

  return (
    <div className="bg-khLightGray">
      <div className={`max-w-page mx-auto px-6 ${padding}`}>
        <div className={`flex flex-col gap-4 md:flex-row md:items-center md:gap-16`}>
          {renderIcon()}
          <div className="flex-1 text-left">
            {eyebrowText && (
              <div className="mb-3">
                <p className="font-albert-sans text-muted-text-gray text-base font-semibold leading-6 tracking-[0.48px]">
                  {eyebrowText}
                </p>
              </div>
            )}
            <div className={`mb-4 flex items-center justify-start ${eyebrowText ? '' : 'gap-4'}`}>
              <h1 className="font-albert-sans text-2xl font-semibold leading-6 text-black md:text-3xl md:leading-6">
                {title}
              </h1>
              {favoriteButton}
            </div>
            {description && (
              <p className="font-open-sans max-w-3xl text-base font-normal leading-7 text-black">{description}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
